import { BaseConstruction } from './BaseConstruction';
import { ConstructionStep } from '../types';
import { Point } from '../objects/Point';
import { Segment } from '../objects/Segment';
import { Circle } from '../objects/Circle';
import { Arc } from '../objects/Arc';
import { DrawPoint, DrawLine, DrawCircle, DrawArc } from '../animations/DrawPoint';
import { MoveCompass, Highlight, Fade } from '../animations/MoveCompass';
import { SVGRenderer } from '../core/SVGRenderer';

// Incenter & Incircle Construction
export class IncenterConstruction extends BaseConstruction {
  public buildSteps(options: { ax?: number; ay?: number; bx?: number; by?: number; cx?: number; cy?: number } = {}): ConstructionStep[] {
    const pA = { x: options.ax ?? 200, y: options.ay ?? 480 };
    const pB = { x: options.bx ?? 550, y: options.by ?? 480 };
    const pC = { x: options.cx ?? 360, y: options.cy ?? 220 };

    const a = SVGRenderer.distance(pB, pC);
    const b = SVGRenderer.distance(pA, pC);
    const c = SVGRenderer.distance(pA, pB);
    const perimeter = a + b + c;

    // Incenter coordinates formula: (a*Ax + b*Bx + c*Cx)/P
    const incenterX = (a * pA.x + b * pB.x + c * pC.x) / perimeter;
    const incenterY = (a * pA.y + b * pB.y + c * pC.y) / perimeter;
    const pIncenter = { x: incenterX, y: incenterY };

    // Incircle radius: Area / semi-perimeter
    const s = perimeter / 2;
    const area = Math.sqrt(s * (s - a) * (s - b) * (s - c));
    const inradius = area / s;

    const pointA = new Point(this.renderer, pA.x, pA.y, 'A');
    const pointB = new Point(this.renderer, pB.x, pB.y, 'B');
    const pointC = new Point(this.renderer, pC.x, pC.y, 'C');
    const pointI = new Point(this.renderer, pIncenter.x, pIncenter.y, 'I (Incenter)');

    const segAB = new Segment(this.renderer, pA, pB, false);
    const segBC = new Segment(this.renderer, pB, pC, false);
    const segCA = new Segment(this.renderer, pC, pA, false);

    const bisectA = new Segment(this.renderer, pA, pIncenter, true);
    const bisectB = new Segment(this.renderer, pB, pIncenter, true);

    const incircle = new Circle(this.renderer, pIncenter, inradius, false);

    [pointA, pointB, pointC, pointI, segAB, segBC, segCA, bisectA, bisectB, incircle].forEach((o) => o.setVisible(false));

    return [
      {
        id: 'step1',
        title: '1. Given Triangle ABC',
        description: 'Draw triangle ABC vertices and sides.',
        narration: 'Step 1: Given triangle ABC.',
        duration: 1500,
        animations: [
          new DrawPoint(pointA),
          new DrawPoint(pointB),
          new DrawPoint(pointC),
          new DrawLine(segAB, this.ruler),
          new DrawLine(segBC, this.ruler),
          new DrawLine(segCA, this.ruler),
        ],
      },
      {
        id: 'step2',
        title: '2. Construct Angle Bisectors of A and B',
        description: 'Construct angle bisectors of angle A and angle B.',
        narration: 'Step 2: Constructing angle bisectors of vertex A and vertex B.',
        duration: 2000,
        animations: [new DrawLine(bisectA, this.ruler), new DrawLine(bisectB, this.ruler)],
      },
      {
        id: 'step3',
        title: '3. Mark Incenter I Intersection',
        description: 'Intersection of angle bisectors defines incenter I.',
        narration: 'Step 3: Marking intersection point I as the incenter.',
        duration: 1200,
        animations: [new DrawPoint(pointI)],
      },
      {
        id: 'step4',
        title: '4. Draw Incircle inside Triangle ABC',
        description: 'Place compass at incenter I with inradius and draw inscribed circle.',
        narration: 'Construction complete! Inscribed circle touching all three sides drawn.',
        duration: 2000,
        animations: [
          new MoveCompass(this.compass, pIncenter, inradius),
          new DrawCircle(incircle, this.compass),
          new Fade(this.compass, false),
          new Highlight(incircle),
        ],
      },
    ];
  }
}

// Circumcenter & Circumcircle Construction
export class CircumcenterConstruction extends BaseConstruction {
  public buildSteps(options: { ax?: number; ay?: number; bx?: number; by?: number; cx?: number; cy?: number } = {}): ConstructionStep[] {
    const pA = { x: options.ax ?? 220, y: options.ay ?? 480 };
    const pB = { x: options.bx ?? 540, y: options.by ?? 480 };
    const pC = { x: options.cx ?? 380, y: options.cy ?? 240 };

    // Circumcenter coordinates
    const D = 2 * (pA.x * (pB.y - pC.y) + pB.x * (pC.y - pA.y) + pC.x * (pA.y - pB.y));
    const Ux = ((pA.x ** 2 + pA.y ** 2) * (pB.y - pC.y) + (pB.x ** 2 + pB.y ** 2) * (pC.y - pA.y) + (pC.x ** 2 + pC.y ** 2) * (pA.y - pB.y)) / D;
    const Uy = ((pA.x ** 2 + pA.y ** 2) * (pC.x - pB.x) + (pB.x ** 2 + pB.y ** 2) * (pA.x - pC.x) + (pC.x ** 2 + pC.y ** 2) * (pB.x - pA.x)) / D;
    const pO = { x: Ux, y: Uy };
    const R = SVGRenderer.distance(pO, pA);

    const pointA = new Point(this.renderer, pA.x, pA.y, 'A');
    const pointB = new Point(this.renderer, pB.x, pB.y, 'B');
    const pointC = new Point(this.renderer, pC.x, pC.y, 'C');
    const pointO = new Point(this.renderer, pO.x, pO.y, 'O (Circumcenter)');

    const segAB = new Segment(this.renderer, pA, pB, false);
    const segBC = new Segment(this.renderer, pB, pC, false);
    const segCA = new Segment(this.renderer, pC, pA, false);

    const circumcircle = new Circle(this.renderer, pO, R, false);

    [pointA, pointB, pointC, pointO, segAB, segBC, segCA, circumcircle].forEach((o) => o.setVisible(false));

    return [
      {
        id: 'step1',
        title: '1. Given Triangle ABC',
        description: 'Draw triangle ABC.',
        narration: 'Step 1: Given triangle ABC.',
        duration: 1500,
        animations: [new DrawPoint(pointA), new DrawPoint(pointB), new DrawPoint(pointC), new DrawLine(segAB), new DrawLine(segBC), new DrawLine(segCA)],
      },
      {
        id: 'step2',
        title: '2. Locate Circumcenter O',
        description: 'Intersection of perpendicular bisectors defines circumcenter O.',
        narration: 'Step 2: Locating circumcenter O.',
        duration: 1200,
        animations: [new DrawPoint(pointO)],
      },
      {
        id: 'step3',
        title: '3. Draw Circumcircle through Vertices A, B, C',
        description: 'Place compass at O with radius R = OA and draw circumscribed circle.',
        narration: 'Construction complete! Circumcircle passing through all three vertices A, B, and C.',
        duration: 2000,
        animations: [
          new MoveCompass(this.compass, pO, R),
          new DrawCircle(circumcircle, this.compass),
          new Fade(this.compass, false),
          new Highlight(circumcircle),
        ],
      },
    ];
  }
}
